import React from 'react';
import { Sparkles, Shield, Globe, Lock } from 'lucide-react';
import { Button } from './ui/button';
import { grantAIConsent } from '../lib/aiConsent';

/**
 * AI Provider Consent Modal
 * Displayed BEFORE any data is sent to third-party AI providers.
 * Required by Apple App Review Guideline 2.1.
 */
const AIConsentModal = ({ open, onAccept, onDecline }) => {
  if (!open) return null;

  const handleAccept = () => {
    grantAIConsent();
    if (onAccept) onAccept();
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
      data-testid="ai-consent-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#0D47A1] to-[#1976D2] flex items-center justify-center">
              <Sparkles className="h-6 w-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900" data-testid="ai-consent-title">
                Utilisation d'une IA tierce
              </h2>
              <p className="text-sm text-slate-500">Information importante avant utilisation</p>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4 text-sm text-slate-700">
          <p>
            Cette fonctionnalité utilise des fournisseurs d'intelligence artificielle
            tiers (<strong>OpenAI</strong> et <strong>Anthropic</strong>) pour traiter
            vos demandes liées à l'immobilier.
          </p>

          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <Globe className="h-5 w-5 text-[#0D47A1] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-900">Données envoyées</p>
                <p className="text-slate-600">
                  Uniquement le <strong>texte</strong> de vos questions
                  (recherches de biens, calculs, conseils immobiliers).
                  Aucune donnée vocale n'est transmise : la reconnaissance
                  vocale s'effectue localement sur votre appareil par iOS,
                  puis le texte transcrit est envoyé.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-[#00B050] flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-900">Confidentialité</p>
                <p className="text-slate-600">
                  Vos identifiants, mot de passe, et informations bancaires
                  ne sont <strong>jamais</strong> transmis à ces fournisseurs.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Lock className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-slate-900">Votre choix</p>
                <p className="text-slate-600">
                  Vous pouvez refuser. Dans ce cas, les fonctionnalités IA
                  (Assistant IA, FAQ IA, génération d'articles) seront
                  désactivées. Vous pourrez accepter plus tard à tout moment.
                </p>
              </div>
            </div>
          </div>

          <p className="text-xs text-slate-500 pt-2 border-t border-slate-100">
            En savoir plus :{' '}
            <a
              href="https://www.10minvestor.com/confidentialite"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#0D47A1] underline"
              data-testid="ai-consent-privacy-link"
            >
              Politique de confidentialité
            </a>
          </p>
        </div>

        <div className="p-6 bg-slate-50 rounded-b-2xl flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            onClick={onDecline}
            className="flex-1"
            data-testid="ai-consent-decline-btn"
          >
            Refuser
          </Button>
          <Button
            onClick={handleAccept}
            className="flex-1 bg-[#0D47A1] hover:bg-[#0A367A] text-white"
            data-testid="ai-consent-accept-btn"
          >
            J'accepte
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AIConsentModal;
